import {BINARY} from '@constants/test';
import type {SelectReducerAction, TestContext, TestState, WriteStatsAction} from '@interfaces/Test';
import type ReducerAction from '@interfaces/ReducerAction';
import type {ReactElement, ReactNode, Reducer} from 'react';
import {createContext, useReducer} from 'react';

interface Props {
    value?: Partial<TestState>;
    children: ReactNode;
}

const actions = {
    reset: 'reset',
    check: 'check',
    complete: 'complete',
    nextQuestion: 'nextQuestion',
    writeStats: 'writeStats',
};

const initialState = {
    id: '',
    type: BINARY,
    showStats: false,
    showProgress: false,
    isComplete: false,
    score: 0,
    questionNum: 0,
    isChecked: false,
    comment: '',
    stats: {},
    selectedOption: undefined,
};

const store = createContext<TestContext>({
    currentState: initialState,
    dispatch: () => {
        /* do nothing */
    },
});
const {Provider} = store;

const reducer: Reducer<TestState, ReducerAction> = (state, action) => {
    switch (action.type) {
        case actions.reset:
            return {
                ...state,
                isComplete: false,
                score: 0,
                questionNum: 0,
                isChecked: false,
                comment: '',
                stats: {},
                selectedOption: undefined,
            };
        case actions.check:
            return {
                ...state,
                isChecked: true,
                selectedOption: (action as SelectReducerAction).data.id,
                score: state.score + (action as SelectReducerAction).data.score,
                comment: (action as SelectReducerAction).data.comment,
            };
        case actions.nextQuestion:
            return {
                ...state,
                isChecked: false,
                questionNum: state.questionNum + 1,
                comment: '',
                stats: {},
                selectedOption: undefined,
            };
        case actions.writeStats:
            return {
                ...state,
                stats: (action as WriteStatsAction).data,
            };
        case actions.complete:
            return {
                ...state,
                isComplete: true,
            };
        default:
            throw new Error('Unknown action');
    }
};

const StateProvider = function ({value, children}: Props): ReactElement<Props> {
    const [currentState, dispatch] = useReducer(reducer, {...initialState, ...(value ?? {})});
    return <Provider value={{currentState, dispatch}}>{children}</Provider>;
};

export {store, StateProvider, actions};
